import { useEffect, useState } from "react";
import { Link, Navigate, NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, LogOut, Users, Settings, ExternalLink, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { http } from "@/lib/api";
import { SCHEMAS } from "@/admin/schema";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [editions, setEditions] = useState([]);
  const [editionId, setEditionId] = useState(localStorage.getItem("fsbe-admin-ed") || "");
  const [open, setOpen] = useState(false);

  const loadEditions = () => http.get("/admin/editions").then((r) => {
    const list = r.data.sort((a, b) => b.year - a.year);
    setEditions(list);
    if (!list.find((e) => e.id === editionId)) setEditionId((list.find((e) => e.current) || list[0])?.id || "");
  });
  useEffect(() => { if (user) loadEditions(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (editionId) localStorage.setItem("fsbe-admin-ed", editionId); }, [editionId]);

  if (user === null) return <p className="p-10" role="status">A verificar sessão…</p>;
  if (!user) return <Navigate to="/admin/login" replace />;
  const isAdmin = user.role === "admin";
  const link = ({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium ${isActive ? "bg-white text-[var(--ink)]" : "text-white/80 hover:bg-white/10"}`;
  const items = Object.entries(SCHEMAS).filter(([, s]) => !s.adminOnly || isAdmin);

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[260px_1fr] bg-[#f1f2f8]">
      <aside className={`${open ? "block" : "hidden"} lg:block bg-[var(--ink)] text-white p-4 lg:sticky lg:top-0 lg:h-screen overflow-y-auto`} data-testid="admin-sidebar">
        <p className="font-display text-xl font-extrabold px-3 pt-2">Painel da Feira</p>
        <p className="px-3 text-xs text-white/60 mt-1" data-testid="admin-user-info">{user.email} · {isAdmin ? "Administrador" : "Editor"}</p>
        <label className="block px-3 mt-5"><span className="eyebrow text-white/60">Edição</span>
          <select value={editionId} onChange={(e) => setEditionId(e.target.value)} className="mt-1 h-10 w-full rounded-lg bg-white/10 border border-white/20 px-2 text-sm" data-testid="admin-edition-select">
            {editions.map((e) => <option key={e.id} value={e.id} className="text-black">{e.title}{e.current ? " (atual)" : ""}</option>)}
          </select></label>
        <nav className="mt-5 space-y-1" onClick={() => setOpen(false)}>
          <NavLink end to="/admin" className={link} data-testid="admin-nav-dashboard"><LayoutDashboard className="h-4 w-4" />Estatísticas</NavLink>
          {items.map(([k, s]) => <NavLink key={k} to={`/admin/${k}`} className={link} data-testid={`admin-nav-${k}`}>{s.label}</NavLink>)}
          {isAdmin && <NavLink to="/admin/utilizadores" className={link} data-testid="admin-nav-users"><Users className="h-4 w-4" />Utilizadores</NavLink>}
          <NavLink to="/admin/definicoes" className={link} data-testid="admin-nav-settings"><Settings className="h-4 w-4" />Definições do site</NavLink>
        </nav>
        <div className="mt-6 border-t border-white/10 pt-4 space-y-1">
          <Link to="/" className="flex items-center gap-3 px-3 py-2.5 text-sm text-white/80 hover:text-white" data-testid="admin-view-site"><ExternalLink className="h-4 w-4" />Ver site público</Link>
          <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 text-sm text-white/80 hover:text-white" data-testid="admin-logout-button"><LogOut className="h-4 w-4" />Terminar sessão</button>
        </div>
      </aside>
      <div className="min-w-0">
        <div className="lg:hidden flex items-center justify-between bg-[var(--ink)] text-white px-4 h-14">
          <span className="font-display font-bold">Painel da Feira</span>
          <button onClick={() => setOpen(!open)} aria-label="Menu do painel" className="h-11 w-11 grid place-items-center" data-testid="admin-mobile-menu"><Menu /></button>
        </div>
        <main className="p-4 sm:p-8"><Outlet context={{ user, isAdmin, editionId, editions, reloadEditions: loadEditions }} /></main>
      </div>
    </div>
  );
}
